import {connect, useDispatch} from "react-redux";
import {
  getUser,
  isAuthenticated,
  isLoading,
} from "../../../store/selectors/AuthSelectors";
import {Formik} from "formik";
import {useState} from "react";
import {useMe} from "../../../hooks/useGetUser";
import Loader from "../shared/Loader";
import * as yup from "yup";
import {
  updateProfileClientAction,
} from "../../../store/actions/AuthActions";
import {Navigate, useNavigate} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import {geocodeByPlaceId} from "react-google-places-autocomplete";
import GooglePlacesAutocomplete from "../shared/SafeGooglePlacesAutocomplete";
import {VerifTelModal} from "./VerifTelModal";
import {formatPhoneNumberIntl} from "react-phone-number-input";
import googleMapsApiKey from "../../../config/googleMaps";
import {
  clearProfileAddressFields,
  normalizeProfileAddressValues,
} from "../../../utils/profileAddress";

const formatDateForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ProfileFormClient = ({isLoading, profileFile, setProfileFile}) => {
  const [selectedAddress, setSelectedAddress] = useState("");
  const [openVerifModal, setOpenVerifModal] = useState(false);

  const schema = yup.object().shape({
    firstName: yup.string().required("Champ obligatoire"),
    birthDate: yup.date().required("Champ obligatoire"),
    lastName: yup.string().required("Champ obligatoire"),
    gender: yup.string().required("Champ obligatoire"),
    email: yup
      .string()
      .required("Champ obligatoire")
      .email("L'e-mail doit être un e-mail valide"),
    phone: yup
      .string()
      .transform((value) => String(value ?? "").trim())
      .required("Champ obligatoire")
      .matches(
        /^\+\d{1,3}(?:[\s.-]*\d){4,14}$/,
        "Le numéro de téléphone doit être au format +[indicatif] [numéro]",
      )
      .test(
        "phone-length",
        "Le numéro de téléphone doit contenir entre 8 et 15 chiffres",
        (value) => {
          const digitCount = String(value || "").replace(/\D/g, "").length;
          return digitCount >= 8 && digitCount <= 15;
        },
      ),
    address: yup
      .string()
      .transform((value) => value ?? "")
      .test(
        "address-length",
        "L'adresse doit contenir au moins 5 caractères",
        (value) => !value || value.length >= 5,
      ),
    city: yup
      .string()
      .nullable()
      .when("address", {
        is: (val) => val && val.length > 0, // Check if address is not empty
        then: (schema) =>
          schema
            .required("Veuillez entrer votre adresse complète")
            .min(2, "La ville doit contenir au moins 2 caractères"),
        otherwise: (schema) => schema.notRequired(),
      }),
    // Google does not always return separate route/street_number components
    // for an already saved address. Hidden fields must not block submission.
    street: yup.string().nullable().notRequired(),
    streetNumber: yup.string().nullable().notRequired(),

    zipCode: yup
      .string()
      .nullable()
      .when("address", {
        is: (val) => val && val.length > 0, // Check if address is not empty
        then: (schema) =>
          schema
            .required("Veuillez entrer votre adresse complète")
            .matches(
              /^\d{5}$/,
              "Le code postal doit contenir exactement 5 chiffres"
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const SubmitUpdate = (values, profileImg) => {
    const normalizedValues = {
      ...normalizeProfileAddressValues(values),
      phone: String(values.phone || "").replace(/[\s.-]/g, ""),
    };

    if (normalizedValues.email !== email) {
      normalizedValues.verifiedEmail = false;
    }

    return dispatch(
      updateProfileClientAction(
        {
          body: {...normalizedValues, profileImg},
          id: id,
        },
        navigate,
        queryClient
      )
    );
  };

  const handlePlaceSelect = async (address, setFieldValue, setFieldTouched) => {
    if (!address) {
      await clearProfileAddressFields(setFieldValue);
      await setFieldTouched("address", true, false);
      setSelectedAddress("");
      return;
    }

    const [place] = await geocodeByPlaceId(address.value.place_id);

    const {long_name: postalCode = ""} =
      place.address_components.find((c) => c.types.includes("postal_code")) ||
      {};
    const {long_name: ville = ""} =
      place.address_components.find((c) => c.types.includes("locality")) || {};
    const street =
      place.address_components.find((c) => c.types.includes("route"))
        ?.long_name || "";
    const streetNumber =
      place.address_components.find((c) => c.types.includes("street_number"))
        ?.long_name || "";

    await Promise.all([
      setFieldValue("streetNumber", streetNumber, false),
      setFieldValue("street", street, false),
      setFieldValue("address", address.label, false),
      setFieldValue("zipCode", postalCode, false),
      setFieldValue("city", ville, false),
    ]);
    await setFieldTouched("address", true, true);
    setSelectedAddress(address);
  };

  const {data, isLoading: ismeLoading, isError} = useMe();

  if (isLoading || ismeLoading) {
    return <Loader />;
  }
  if (isError) {
    return Navigate("/login");
  }
  if (data) {
  }

  const {
    firstName,
    lastName,
    email,
    gender,
    birthDate,
    phone,
    id,
    address,
    city,
    zipCode,
  } = data;
  const birthDate2 = formatDateForInput(birthDate);

  return (
    <div className="!pt-3 py-5 w-full">
      <div className="settings-form">
        <Formik
          validateOnBlur={true}
          validateOnChange={false}
          enableReinitialize
          initialValues={{
            firstName,
            lastName,
            email,
            gender: ["male", "female", "other"].includes(gender) ? gender : "",
            birthDate: birthDate2,
            phone: formatPhoneNumberIntl(phone || "") || phone || "",
            address: address ?? "",
            street: "",
            streetNumber: "",
            city: city ?? "",
            zipCode: zipCode ?? "",
          }}
          validationSchema={schema}
          onSubmit={async (values, {setSubmitting}) => {
            const submittedPhone = String(values.phone || "").replace(/[\s.-]/g, "");
            const currentPhone = String(phone || "").replace(/[\s.-]/g, "");
            if (submittedPhone !== currentPhone) {
              setOpenVerifModal(true);
              setSubmitting(false);
            } else {
              try {
                await SubmitUpdate(values, profileFile);
                setProfileFile(null);
                setSelectedAddress("");
              } catch {
                // The update action already displays the API error.
              } finally {
                setSubmitting(false);
              }
            }
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            resetForm,
            setFieldValue,
            setFieldTouched,
            touched,
            dirty,
          }) => (
            <form onSubmit={handleSubmit}>
              <VerifTelModal
                email={values.email}
                handleSubmit={async () => {
                  try {
                    await SubmitUpdate(values, profileFile);
                    setProfileFile(null);
                    setSelectedAddress("");
                    setOpenVerifModal(false);
                  } catch {
                    // The update action already displays the API error.
                  }
                }}
                phone={values.phone}
                setShow={setOpenVerifModal}
                show={openVerifModal}
              />
              <div className="row !m-0">
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="client-lastName">Nom</label>
                  <input
                    id="client-lastName"
                    type="text"
                    className="form-control"
                    name="lastName"
                    onChange={handleChange}
                    onBlur={(event) => {
                      handleBlur(event);
                      const formatted = formatPhoneNumberIntl(event.target.value);
                      if (formatted) setFieldValue("phone", formatted);
                    }}
                    value={values.lastName}
                  />
                  {touched.lastName && errors.lastName && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.lastName}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="client-firstName">Prénom</label>
                  <input
                    id="client-firstName"
                    type="text"
                    className="form-control"
                    name="firstName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.firstName}
                  />
                  {touched.firstName && errors.firstName && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.firstName}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="client-email">Email</label>
                  <input
                    id="client-email"
                    type="email"
                    name="email"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                  />
                  {errors.email && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.email}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="client-phone">Numéro de téléphone</label>
                  <input
                    id="client-phone"
                    type="text"
                    className="form-control !text-[#333333]"
                    name="phone"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phone}
                  />
                  {errors.phone && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.phone}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="client-birthDate">Date de naissance</label>
                  <input
                    id="client-birthDate"
                    type="date"
                    className="form-control"
                    name="birthDate"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.birthDate}
                  />
                  {errors.birthDate && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.birthDate}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="client-gender">Genre</label>

                  <select
                    id="client-gender"
                    className="form-control"
                    name="gender"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.gender}
                  >
                    <option value={"female"}>Femme</option>
                    <option value={"male"}>Homme</option>
                    <option value={"other"}>Autre</option>
                  </select>
                  {errors.gender && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.gender}
                    </div>
                  )}
                </div>

                <div className="form-group mb-3 col-md-12">
                  <label className="form-label" htmlFor="client-address">Adresse</label>
                  <GooglePlacesAutocomplete
                    i18nIsDynamicList={true}
                    apiKey={googleMapsApiKey}
                    autocompletionRequest={{
                      componentRestrictions: {country: ["fr"]},
                      types: ["address"],
                    }}
                    selectProps={{
                      inputId: "client-address",
                      noOptionsMessage: () => "Aucune option disponible",
                      value:
                        selectedAddress ||
                        (values.address
                          ? {
                              label: values.address,
                              value: {place_id: null},
                            }
                          : null),
                      isClearable: true,
                      backspaceRemovesValue: true,
                      escapeClearsValue: true,
                      onChange: (address) =>
                        handlePlaceSelect(
                          address,
                          setFieldValue,
                          setFieldTouched
                        ),
                      placeholder: "Recherchez une adresse...",
                      styles: {
                        dropdownIndicator: (base) => ({
                          ...base,
                          display: "none", // Hides the dropdown icon
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: "none", // Hides the separator line
                        }),
                        control: (base) => ({
                          ...base,
                          borderColor: "#eee", // Matches the other inputs' border color
                          borderWidth: "1px",
                          borderRadius: "0.375rem", // Rounded corners like your other inputs
                          padding: "0.375rem 0.75rem", // Add padding to match the input
                          boxShadow: "none",
                        }),
                        input: (base) => ({
                          ...base,
                          fontSize: "0.875rem", // Matches your other inputs' font size
                          color: "#374151", // Text color
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#9ca3af", // Placeholder color
                          fontSize: "0.875rem",
                        }),
                      },
                    }}
                  />
                  {touched.address &&
                    (errors.address ||
                      errors.postCode ||
                      errors.ville ||
                      errors.street ||
                      errors.streetNumber) && (
                      <div
                        className="invalid-feedback animated fadeInUp"
                        style={{display: "block"}}
                      >
                        {errors.address ||
                          errors.postCode ||
                          errors.ville ||
                          errors.street ||
                          errors.streetNumber}
                      </div>
                    )}
                </div>
              </div>
              <div className="flex justify-center gap-1 lg:gap-3 mt-4 lg:justify-end flex-wrap-reverse px-[15px] ">
                <button
                  className="btn !min-w-[250px] !rounded-lg bg-white !text-[#0D44B1] !border-[#0D44B1] !border border-solid"
                  type="reset"
                  onClick={() => {
                    resetForm();
                    setSelectedAddress("");
                    setProfileFile(null);
                  }}
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  className="btn !min-w-[250px] !rounded-lg bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white"
                  type="submit"
                  disabled={isSubmitting || (!dirty && !profileFile)}
                >
                  {isSubmitting ? "Enregistrement..." : "Enregistrer"}{" "}
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};
const mapStateToProps = (state) => {
  return {
    isAuthenticated: isAuthenticated(state),
    isLoading: isLoading(state),
    user: getUser(state),
  };
};
export default connect(mapStateToProps)(ProfileFormClient);
